import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import {
  DEFAULT_DOLLAR_BIN_SIZE,
  computeDensityBins,
  calculateRcrLocal as calculateRcrLocalDomain,
} from './domain/computeDensityBins'

function toLongLots(ledger) {
  return Object.values(ledger || {})
    .filter((u) => u && u.bucket === 'manual' && Number(u.current?.qty || 0) > 0)
    .map((u) => ({
      price: Number(u.current?.avgPrice || 0),
      qty: Number(u.current?.qty || 0),
    }))
}

function toShortLots(shortLedger) {
  return Object.values(shortLedger || {})
    .filter((u) => u && u.status === 'active' && Number(u.remainingQty || u.origin?.qty || 0) > 0)
    .map((u) => ({
      price: Number(u.origin?.sellPrice || u.sellPrice || 0),
      qty: Number(u.remainingQty || u.origin?.qty || 0),
    }))
}

export function useInventoryProfile() {
  const store = useStore()

  const visible = ref(true)
  const isTargetMode = ref(false)
  const isRangeMode = ref(false)
  const dollarBinSize = ref(DEFAULT_DOLLAR_BIN_SIZE)

  const marketPrice = computed(
    () => store.state.marketData?.btcUsd || store.state.marketData?.price || 0
  )
  const targetPct = computed(() => store.getters.targetPctFrac || 0.006)
  const marginInputPct = computed({
    get: () => Number(store.state.targetMarginPct) || 0.6,
    set: (val) => store.commit('setTargetMarginPct', val),
  })

  const longs = computed(() => toLongLots(store.state.ledger))
  const shorts = computed(() => toShortLots(store.state.shortLedger))

  const densityBins = computed(() =>
    computeDensityBins({
      longs: longs.value,
      shorts: shorts.value,
      marketPrice: marketPrice.value,
      isTargetMode: isTargetMode.value,
      targetPct: targetPct.value,
      dollarBinSize: dollarBinSize.value,
    })
  )

  function toggleVisible() {
    visible.value = !visible.value
  }

  function toggleTargetMode() {
    isTargetMode.value = !isTargetMode.value
  }

  function toggleRangeMode() {
    isRangeMode.value = !isRangeMode.value
    if (isRangeMode.value && !visible.value) {
      visible.value = true
    }
  }

  function calculateRcrLocal(proposedLongQty) {
    return calculateRcrLocalDomain(shorts.value, marketPrice.value, proposedLongQty)
  }

  function handleKeyDown(e) {
    if (e.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return
    if (e.key === 'i' || e.key === 'I') toggleVisible()
    if (e.key === 't' || e.key === 'T') toggleTargetMode()
    if (e.key === 's' || e.key === 'S') toggleRangeMode()
  }

  onMounted(() => {
    window.addEventListener('keydown', handleKeyDown)
  })

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyDown)
  })

  return {
    visible,
    isTargetMode,
    isRangeMode,
    dollarBinSize,
    marginInputPct,
    marketPrice,
    densityBins,
    toggleVisible,
    toggleTargetMode,
    toggleRangeMode,
    calculateRcrLocal,
  }
}
